import React, { useMemo, useState } from 'react';

export type OperationsGuideAction =
  | 'status'
  | 'demo'
  | 'calibration'
  | 'fine_tuning'
  | 'new_facility';

export interface OperationsReadiness {
  schema_version: number;
  checked_at: string;
  facility: { id: string | null; name: string; version: number | null };
  role: 'admin' | 'operator' | string;
  model: { ready: boolean; mode: string; adapter_id: string | null; adapter_version: number | null };
  tracking: {
    available: boolean;
    running: boolean;
    completed: boolean;
    mode: string;
    phase: string | null;
    row_count: number;
  };
  calibration_package: {
    ready: boolean;
    latest: null | {
      package_id: string | null;
      status: string;
      eligible_for_fine_tuning: boolean;
      gates: Record<string, boolean>;
      tracking: {
        row_count: number;
        accepted_calibration_samples: number;
        final_hvac_cvrmse_pct: number | null;
        final_temperature_mae_c: number | null;
      };
      internal_gain_schedule: { patched_hours: number };
    };
  };
  policy: { ready: boolean; id: string | null };
  permissions: { can_start_fine_tuning: boolean; can_run_simulation_calibration: boolean };
  recommended_action: string;
  summary: string;
}

interface OperationsGuideProps {
  readiness: OperationsReadiness | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onAction: (action: OperationsGuideAction) => void;
  onDismiss: () => void;
}

const GATE_LABELS: Record<string, string> = {
  tracking_complete: '충분한 비교 데이터',
  calibrated_mode: '자동 보정 모드',
  accepted_calibration_samples: '보정값 반영 횟수',
  hvac_cvrmse: '에너지 오차 기준',
  temperature_mae: '온도 오차 기준',
  facility_binding: '현재 시설 일치',
  adapter_binding: '현재 EnergyPlus 모델 일치',
};
const purposeOptions: Array<{
  id: OperationsGuideAction;
  icon: string;
  title: string;
  description: string;
}> = [
  { id: 'status', icon: 'fa-gauge-high', title: '현재 건물 상태 확인', description: '센서와 주요 설비 상태를 먼저 살펴봅니다.' },
  { id: 'demo', icon: 'fa-play', title: '기능 시연', description: 'EnergyPlus에서 Tracking과 AI 제어 흐름을 안전하게 실행합니다.' },
  { id: 'calibration', icon: 'fa-sliders', title: '가상 건물 정확도 확인', description: '실제·가상 데이터를 비교하고 자동 보정 결과를 저장합니다.' },
  { id: 'fine_tuning', icon: 'fa-brain', title: 'AI 제어모델 개선', description: '검증된 보정 결과로 별도의 후보 모델을 추가 학습합니다.' },
  { id: 'new_facility', icon: 'fa-building-circle-check', title: '새 시설 등록', description: '시설 정보, IDF·EPW, 센서 포인트와 계통도를 설정합니다.' },
];

const statusTone = (ok: boolean, active = false) => (
  active
    ? 'border-[#ecd098] bg-[#fff8ea] text-[#88601b]'
    : ok
      ? 'border-[#b9ddd5] bg-[#eff9f6] text-[#247565]'
      : 'border-[#d9dee1] bg-[#f6f7f7] text-[#778189]'
);

export const OperationsGuide: React.FC<OperationsGuideProps> = ({
  readiness,
  loading,
  error,
  onRefresh,
  onAction,
  onDismiss,
}) => {
  const [selected, setSelected] = useState<OperationsGuideAction | null>(null);
  const failedGates = useMemo(() => Object.entries(readiness?.calibration_package.latest?.gates ?? {})
    .filter(([, passed]) => !passed)
    .map(([key]) => GATE_LABELS[key] ?? key), [readiness]);

  const selectionText = selected === 'demo'
    ? {
      title: '일반 시연을 시작할까요?',
      body: 'EnergyPlus 가상 BEMS에서 Tracking과 AI 제어를 실행합니다. 실제 현장 설비에는 제어값을 쓰지 않습니다.',
      button: '안전한 시연 시작',
    }
    : selected === 'calibration'
      ? {
        title: '가상 건물 자동 보정을 시작할까요?',
        body: '하루 분량의 시뮬레이션을 비교하여 내부발열 조건을 보정합니다. 완료 후 정확도 기준을 통과하면 Fine-tuning용 결과가 자동 저장됩니다.',
        button: '자동 보정 시작',
      }
      : null;

  return (
    <section className="rounded-2xl border border-[#b9d8d4] bg-white p-3.5 shadow-[0_10px_28px_rgba(43,74,78,0.08)]" aria-label="범비 운영 준비 도우미">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-[#e6f5f2] text-[#08777d]"><i className="fa-solid fa-route text-xs" /></span>
            <div>
              <p className="text-[10px] font-semibold text-[#08777d]">범비 운영 준비 도우미</p>
              <h3 className="text-sm font-semibold text-[#20272d]">무엇을 하려는지 선택해 주세요</h3>
            </div>
          </div>
          <p className="mt-2 text-[10px] leading-4 text-[#65717a]">
            전문 설정은 범비가 먼저 확인합니다. 사용자는 목적만 선택하면 됩니다.
          </p>
        </div>
        <button type="button" onClick={onDismiss} className="h-7 w-7 rounded-lg text-[#8a949b] hover:bg-black/5" aria-label="운영 준비 안내 닫기">
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      {loading && (
        <div className="mt-3 rounded-xl border border-[#e0e4e6] bg-[#f7f8f8] px-3 py-4 text-center text-[10px] text-[#66717a]">
          <i className="fa-solid fa-circle-notch fa-spin mr-2 text-[#08777d]" />현재 시설 상태를 확인하고 있습니다.
        </div>
      )}

      {!loading && error && (
        <div className="mt-3 rounded-xl border border-[#ecc7c7] bg-[#fff5f5] p-3 text-[10px] text-[#a34d4d]">
          <p>{error}</p>
          <button type="button" onClick={onRefresh} className="mt-2 rounded-lg border border-[#dfb1b1] bg-white px-2.5 py-1.5 font-semibold">다시 확인</button>
        </div>
      )}

      {!loading && readiness && (
        <>
          <div className="mt-3 rounded-xl border border-[#dce3e5] bg-[#f7f9f9] p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-[11px] font-semibold text-[#263137]">{readiness.facility.name}</p>
                <p className="text-[9px] text-[#7b858d]">시설 설정 v{readiness.facility.version ?? '-'} · {readiness.role === 'admin' ? '관리자' : '운영자'}</p>
              </div>
              <button type="button" onClick={onRefresh} className="shrink-0 rounded-lg border border-[#d8dddf] bg-white px-2 py-1.5 text-[9px] font-semibold text-[#66717a] hover:text-[#08777d]">
                <i className="fa-solid fa-rotate mr-1" />새로 확인
              </button>
            </div>
            <p className="mt-2 text-[10px] leading-4 text-[#4f5c64]">{readiness.summary}</p>
            <div className="mt-3 grid grid-cols-2 gap-1.5">
              {[
                { label: '시설 모델', ok: readiness.model.ready, active: false, detail: readiness.model.ready ? '연결 완료' : '설정 필요' },
                { label: 'Tracking', ok: readiness.tracking.completed, active: readiness.tracking.running, detail: readiness.tracking.running ? '진행 중' : readiness.tracking.completed ? '결과 있음' : '실행 전' },
                { label: '보정 결과', ok: readiness.calibration_package.ready, active: false, detail: readiness.calibration_package.ready ? '검증 완료' : '생성 필요' },
                { label: '운영 AI', ok: readiness.policy.ready, active: false, detail: readiness.policy.ready ? '적용 가능' : '정책 확인 필요' },
              ].map(item => (
                <div key={item.label} className={`rounded-lg border px-2.5 py-2 ${statusTone(item.ok, item.active)}`}>
                  <p className="text-[8px] font-semibold opacity-75">{item.label}</p>
                  <p className="mt-0.5 text-[10px] font-bold"><i className={`fa-solid ${item.active ? 'fa-spinner fa-spin' : item.ok ? 'fa-circle-check' : 'fa-circle'} mr-1`} />{item.detail}</p>
                </div>
              ))}
            </div>
            {failedGates.length > 0 && !readiness.calibration_package.ready && (
              <p className="mt-2 rounded-lg bg-[#fff7e8] px-2.5 py-2 text-[9px] leading-4 text-[#85601f]">
                최근 결과에서 다시 확인할 항목: {failedGates.join(', ')}
              </p>
            )}
          </div>

          {!selectionText && (
            <div className="mt-3 grid gap-2">
              {purposeOptions.map(option => {
                const disabled = option.id === 'calibration' && !readiness.model.ready;
                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (option.id === 'demo' || option.id === 'calibration') setSelected(option.id);
                      else onAction(option.id);
                    }}
                    className="flex items-start gap-3 rounded-xl border border-[#dce1e3] bg-white p-3 text-left transition hover:border-[#8fc7c3] hover:bg-[#f5fbfa] disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#edf6f5] text-[#08777d]"><i className={`fa-solid ${option.icon} text-xs`} /></span>
                    <span className="min-w-0">
                      <span className="block text-[11px] font-semibold text-[#2a3338]">{option.title}</span>
                      <span className="mt-0.5 block text-[9px] leading-4 text-[#748088]">{option.description}</span>
                      {option.id === 'fine_tuning' && !readiness.calibration_package.ready && (
                        <span className="mt-1 block text-[9px] font-semibold text-[#a16b19]">먼저 ‘가상 건물 정확도 확인’이 필요합니다.</span>
                      )}
                      {option.id === 'fine_tuning' && !readiness.permissions.can_start_fine_tuning && (
                        <span className="mt-1 block text-[9px] font-semibold text-[#65717a]">학습 시작은 관리자 승인이 필요합니다.</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {selectionText && selected && (
            <div className="mt-3 rounded-xl border border-[#a9d2cd] bg-[#f2faf8] p-3">
              <p className="text-[11px] font-semibold text-[#225f59]">{selectionText.title}</p>
              <p className="mt-1.5 text-[9px] leading-4 text-[#58716e]">{selectionText.body}</p>
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={() => setSelected(null)} className="flex-1 rounded-lg border border-[#cbd8d6] bg-white px-3 py-2 text-[10px] font-semibold text-[#68747a]">이전</button>
                <button type="button" onClick={() => onAction(selected)} className="flex-[1.6] rounded-lg bg-[#08777d] px-3 py-2 text-[10px] font-semibold text-white hover:bg-[#06686d]">
                  <i className="fa-solid fa-play mr-1.5" />{selectionText.button}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};
